// Motor de resortes (springs) al estilo Apple: damping + response en lugar de masa/rigidez.
// - Siempre parte del valor presente (interrumpible): llamar go() a mitad de una animacion
//   re-dirige el movimiento conservando la velocidad actual, sin saltos.
// - damping 1 = sin rebote (por defecto). ~0.8 solo despues de un gesto con impulso.
// - response = que tan rapido llega (segundos). No es una duracion fija.

export const reducedMotion = () =>
  typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;

export type SpringOptions = {
  damping?: number;
  response?: number;
  velocity?: number;
  done?: () => void;
};

type Scheduler = {
  request: (cb: (now: number) => void) => number;
  cancel: (id: number) => void;
  now: () => number;
};

const defaultScheduler: Scheduler = {
  request: (cb) => requestAnimationFrame(cb),
  cancel: (id) => cancelAnimationFrame(id),
  now: () => performance.now(),
};

// Integra un paso del resorte. Separado para poder probarlo sin navegador.
export function stepSpring(
  state: { value: number; velocity: number },
  target: number,
  dt: number,
  damping: number,
  response: number,
) {
  const stiffness = Math.pow((2 * Math.PI) / response, 2);
  const friction = (4 * Math.PI * damping) / response;
  const steps = Math.max(1, Math.ceil(dt * 240));
  const h = dt / steps;
  let { value, velocity } = state;

  for (let i = 0; i < steps; i++) {
    const accel = -stiffness * (value - target) - friction * velocity;
    velocity += accel * h;
    value += velocity * h;
  }
  return { value, velocity };
}

export class SpringValue {
  value: number;
  velocity = 0;
  target: number;
  private frame = 0;
  private token = 0;

  constructor(
    initial: number,
    private readonly apply: (v: number) => void,
    private readonly epsilon = 0.01,
    private readonly scheduler: Scheduler = defaultScheduler,
  ) {
    this.value = initial;
    this.target = initial;
  }

  get animating() {
    return this.frame !== 0;
  }

  set(v: number) {
    this.stop();
    this.value = this.target = v;
    this.velocity = 0;
    this.apply(v);
    return this;
  }

  stop() {
    if (this.frame) this.scheduler.cancel(this.frame);
    this.frame = 0;
    this.token++;
  }

  go(target: number, { damping = 1, response = 0.35, velocity, done }: SpringOptions = {}) {
    this.target = target;
    if (velocity !== undefined) this.velocity = velocity;
    this.stop();
    const token = this.token;

    if (reducedMotion()) {
      this.value = target;
      this.velocity = 0;
      this.apply(target);
      done?.();
      return this;
    }
    let last = this.scheduler.now();
    const tick = (now: number) => {
      if (token !== this.token) return;
      const dt = Math.min(0.064, Math.max(0, (now - last) / 1000));
      last = now;
      const next = stepSpring(this, target, dt, damping, response);
      this.value = next.value;
      this.velocity = next.velocity;

      if (Math.abs(this.value - target) < this.epsilon && Math.abs(this.velocity) < this.epsilon * 12) {
        this.value = target;
        this.velocity = 0;
        this.frame = 0;
        this.apply(target);
        done?.();
        return;
      }
      this.apply(this.value);
      this.frame = this.scheduler.request(tick);
    };
    this.frame = this.scheduler.request(tick);
    return this;
  }
}

// Proyeccion de impulso (la misma funcion que usa Apple): a donde llegaria lo lanzado.
export const project = (velocity: number, deceleration = 0.998) =>
  ((velocity / 1000) * deceleration) / (1 - deceleration);

// Resistencia elastica al pasar un limite: cuanto mas se pasa, menos sigue al dedo.
export const rubberband = (overshoot: number, dimension: number, constant = 0.55) =>
  (overshoot * dimension * constant) / (dimension + constant * Math.abs(overshoot));

// Historial corto de posiciones para medir la velocidad al soltar.
export class VelocityTracker {
  private samples: { t: number; x: number; y: number }[] = [];

  add(x: number, y: number, t = performance.now()) {
    this.samples.push({ t, x, y });
    const cutoff = t - 100;
    while (this.samples.length > 2 && this.samples[0].t < cutoff) this.samples.shift();
  }

  velocity() {
    const s = this.samples;
    if (s.length < 2) return { x: 0, y: 0 };
    const a = s[0];
    const b = s[s.length - 1];
    const dt = (b.t - a.t) / 1000;
    if (dt <= 0) return { x: 0, y: 0 };
    return { x: (b.x - a.x) / dt, y: (b.y - a.y) / dt };
  }
}

// Entrada suave de un elemento recien montado (opacidad + desplazamiento corto).
export function enter(el: HTMLElement | null, { x = 0, y = 10, scale = 1, response = 0.34 } = {}) {
  if (!el) return;
  const reduce = reducedMotion();
  el.style.opacity = '0';
  if (!reduce) el.style.transform = `translate(${x}px,${y}px) scale(${scale})`;
  new SpringValue(0, (p) => {
    el.style.opacity = String(Math.min(1, p * 1.15));
    if (!reduce) el.style.transform = `translate(${x * (1 - p)}px,${y * (1 - p)}px) scale(${scale + (1 - scale) * p})`;
  }, 0.001).go(1, {
    response: reduce ? 0.2 : response,
    done: () => {
      el.style.transform = '';
      el.style.opacity = '';
    },
  });
}
